import { useBusinessEntitiesOfCompany } from '@commenda/api';
import { isComplianceLaunched } from '@commenda/feature-launches';
import { RadioCheckedIcon, RadioUncheckedIcon } from '@commenda/icons';
import {
  Badge,
  Button,
  CountryCode,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FlagComponent,
  Icons,
  Input,
} from '@commenda/kakashi';
import { IBusinessEntity } from '@commenda/types';
import { cn, getPrintableCorporationType } from '@commenda/utils';
import { Country } from 'country-state-city';
import { useState } from 'react';

const ChooseEntityModalBusinessEntity = ({
  entity,
  onSelect,
  selectedEntityId,
}: {
  entity: IBusinessEntity;
  onSelect: (entityId: number) => void;
  selectedEntityId: number | null;
}) => {
  const isSupportedComplianceEntity = isComplianceLaunched(
    entity.generalCorporationInfo.incorporationCountry
  );

  return (
    <div
      key={entity.id}
      className={cn(
        'flex items-center gap-lg cursor-pointer',
        !isSupportedComplianceEntity
          ? 'opacity-60 pointer-events-none select-none'
          : ''
      )}
      onClick={() => onSelect(entity.id)}
    >
      {entity.id === selectedEntityId ? (
        <RadioCheckedIcon className="size-5" />
      ) : (
        <RadioUncheckedIcon className="size-5" />
      )}
      <FlagComponent
        country={
          entity.generalCorporationInfo.incorporationCountry as CountryCode
        }
        countryName={
          Country.getCountryByCode(
            entity.generalCorporationInfo.incorporationCountry
          )?.name
        }
      />
      <span className="txt-medium-medium text-text-secondary">
        {entity.generalCorporationInfo.legalName}
        {!isSupportedComplianceEntity && ` (Unsupported)`}
      </span>
      <Badge size="sm">
        {getPrintableCorporationType(
          entity.generalCorporationInfo.businessEntityType,
          entity.generalCorporationInfo.incorporationCountry
        )}
      </Badge>
    </div>
  );
};

export const ChooseEntityModal = ({
  isOpen,
  onClose,
  companyId,
  onContinue,
  onAddEntity,
  disabled,
  title = 'Choose an entity',
  description = 'A compliance calendar will be generated for the selected entity.',
}: {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  onContinue: (entityId: number) => void;
  onAddEntity?: () => void;
  disabled?: boolean;
  title?: string;
  description?: string;
}) => {
  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const { data: businessEntitiesResponse } =
    useBusinessEntitiesOfCompany(companyId);

  const businessEntities =
    businessEntitiesResponse?.entities?.filter(
      (entity) =>
        !entity.featureSpecificInfo?.incorporation?.isIncorporationInProgress
    ) ?? [];

  const filteredBusinessEntities = businessEntities
    .filter((entity) =>
      entity.generalCorporationInfo.legalName
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase())
    )
    .sort((a) => {
      const isSupportedA = isComplianceLaunched(
        a.generalCorporationInfo.incorporationCountry
      );

      return isSupportedA ? -1 : 1;
    });

  const handleClose = () => {
    setSelectedEntityId(null);
    setSearchQuery('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent preventCloseOnOutsideClick>
        <DialogHeader className="flex flex-col gap-xs items-start">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="p-3xl flex flex-col gap-2xl">
          <Input
            placeholder="Search entity"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftEl={() => <Icons.SearchMd className="size-5" />}
          />
          <div className="flex flex-col gap-2xl max-h-[320px] overflow-y-auto">
            {filteredBusinessEntities.length ? (
              filteredBusinessEntities.map((entity) => (
                <ChooseEntityModalBusinessEntity
                  key={entity.id}
                  entity={entity}
                  onSelect={setSelectedEntityId}
                  selectedEntityId={selectedEntityId}
                />
              ))
            ) : (
              <div className="txt-medium-medium text-text-secondary">
                No entities found
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="flex items-center justify-between w-full">
          {onAddEntity ? (
            <Button variant="secondary" onClick={onAddEntity} size="sm">
              <Icons.Plus className="size-4" />
              <span>Add entity</span>
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-lg">
            <Button variant="secondary" onClick={handleClose} size="sm">
              Cancel
            </Button>
            <Button
              onClick={() => onContinue(selectedEntityId)}
              disabled={!selectedEntityId || disabled}
              size="sm"
            >
              Continue
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
